import { useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { UploadCloud, X, Loader2, ImageIcon } from "lucide-react";

/**
 * ImageUploader
 *  - Drag-and-drop OR click to upload an image to Cloudinary (signed upload).
 *  - On success calls onChange(url) with the Cloudinary secure_url.
 *  - If `value` is provided, shows a thumbnail preview with a remove button.
 *
 * Props:
 *   value: current image URL (string)
 *   onChange: (url | "") => void
 *   folder: one of "coastal-bend/projects" | "coastal-bend/floor-plans" | "coastal-bend/testimonials"
 *   aspect: tailwind aspect class (default "aspect-[4/3]")
 *   testid: base data-testid
 */
export default function ImageUploader({ value, onChange, folder = "coastal-bend/projects", aspect = "aspect-[4/3]", testid = "image" }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError("Please choose an image file.");
    if (file.size > 10 * 1024 * 1024) return setError("Image must be under 10 MB.");

    setBusy(true);
    setError("");
    setProgress(0);
    try {
      // 1) get signature from backend
      const { data: sig } = await api.get(`/cloudinary/signature?folder=${encodeURIComponent(folder)}`);

      // 2) upload directly to Cloudinary
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
      };
      xhr.onload = () => {
        setBusy(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          const res = JSON.parse(xhr.responseText);
          onChange(res.secure_url);
        } else {
          let detail = "Upload failed";
          try { detail = JSON.parse(xhr.responseText).error?.message || detail; } catch {}
          setError(detail);
        }
      };
      xhr.onerror = () => { setBusy(false); setError("Network error during upload."); };
      xhr.send(form);
    } catch (e) {
      setBusy(false);
      setError(e.response?.data?.detail || e.message || "Upload failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    doUpload(file);
  };

  const clear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange("");
  };

  if (value) {
    return (
      <div className={`relative ${aspect} w-full rounded-lg overflow-hidden group`} data-testid={`${testid}-preview`}>
        <img src={value} alt="uploaded" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`${testid}-replace`}>
            <UploadCloud size={13}/> Replace
          </button>
          <button type="button" onClick={clear} className="btn-ghost !py-1.5 !px-3 !text-xs" style={{color:'#fca5a5'}} data-testid={`${testid}-remove`}>
            <X size={13}/> Remove
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
      </div>
    );
  }

  return (
    <div>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative ${aspect} w-full rounded-lg border-2 border-dashed cursor-pointer transition-colors flex flex-col items-center justify-center text-center p-6`}
        style={{
          borderColor: dragging ? 'var(--accent)' : 'rgba(255,255,255,0.12)',
          background: dragging ? 'rgba(212,163,115,0.06)' : 'rgba(11,19,43,0.4)',
        }}
        data-testid={`${testid}-dropzone`}
      >
        {busy ? (
          <>
            <Loader2 size={28} className="animate-spin mb-3" style={{color:'var(--accent)'}}/>
            <div className="text-sm font-medium">Uploading… {progress}%</div>
            <div className="w-full max-w-[200px] h-1 mt-3 rounded-full overflow-hidden" style={{background: 'rgba(255,255,255,0.08)'}}>
              <div className="h-full transition-all" style={{width: `${progress}%`, background: 'var(--accent)'}}/>
            </div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{background: 'rgba(212,163,115,0.12)'}}>
              <UploadCloud size={22} style={{color:'var(--accent)'}} />
            </div>
            <div className="font-medium text-sm">Drag &amp; drop an image</div>
            <div className="text-xs mt-1" style={{color:'var(--text-muted)'}}>or click to choose · JPG/PNG · up to 10 MB</div>
          </>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => doUpload(e.target.files?.[0])} data-testid={`${testid}-input`} />
      </label>
      {error && <div className="text-xs mt-2" style={{color:'#fca5a5'}} data-testid={`${testid}-error`}>{error}</div>}
    </div>
  );
}
